import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { 
  Zap, 
  Target, 
  TrendingUp, 
  Search, 
  FileText, 
  Award,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';

function LandingPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  // If user is signed in, redirect to dashboard
  React.useEffect(() => {
    if (isSignedIn) {
      navigate('/dashboard');
    }
  }, [isSignedIn, navigate]);

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      padding: '20px 40px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    },
    logo: {
      fontSize: '24px',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    nav: {
      display: 'flex',
      gap: '32px',
      alignItems: 'center'
    },
    navLink: {
      color: 'rgba(255, 255, 255, 0.7)',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'color 0.2s'
    },
    hero: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '100px 40px',
      textAlign: 'center'
    },
    heroTitle: {
      fontSize: '56px',
      fontWeight: '800',
      marginBottom: '24px',
      lineHeight: '1.2'
    },
    gradient: {
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    heroSubtitle: {
      fontSize: '20px',
      color: 'rgba(255, 255, 255, 0.7)',
      marginBottom: '40px',
      maxWidth: '700px',
      margin: '0 auto 40px'
    },
    ctaButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      color: 'white',
      padding: '16px 32px',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)'
    },
    features: {
      maxWidth: '1200px',
      margin: '80px auto',
      padding: '0 40px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '32px'
    },
    featureCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '32px',
      transition: 'transform 0.2s, border-color 0.2s'
    },
    featureIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px'
    },
    featureTitle: {
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '12px'
    },
    featureDescription: {
      fontSize: '15px',
      color: 'rgba(255, 255, 255, 0.7)',
      lineHeight: '1.6'
    },
    benefits: {
      maxWidth: '1200px',
      margin: '80px auto',
      padding: '0 40px'
    },
    benefitsTitle: {
      fontSize: '36px',
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: '48px'
    },
    benefitsList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '24px'
    },
    benefitItem: {
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start'
    },
    footer: {
      maxWidth: '1200px',
      margin: '100px auto 0',
      padding: '40px 40px',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '20px'
    },
    footerLinks: {
      display: 'flex',
      gap: '32px',
      flexWrap: 'wrap'
    },
    footerLink: {
      color: 'rgba(255, 255, 255, 0.6)',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.2s'
    }
  };

  const features = [
    {
      icon: <Sparkles size={24} />,
      title: 'AI Campaign Generator',
      description: 'Generate complete Google Ads campaigns with AI-powered keyword research and ad copy in multiple languages.'
    },
    {
      icon: <TrendingUp size={24} />,
      title: 'Smart Bid Optimization',
      description: 'Automated bid adjustments based on real-time performance data and conversion tracking.'
    },
    {
      icon: <Award size={24} />,
      title: 'Quality Score Optimizer',
      description: 'Analyze and improve Quality Scores with AI-generated suggestions for better ad relevance and CTR.'
    },
    {
      icon: <Search size={24} />,
      title: 'Multi-Language Keyword Research',
      description: 'Discover high-performing keywords across multiple languages from a single seed keyword.'
    },
    {
      icon: <FileText size={24} />,
      title: 'Professional Ad Copy',
      description: 'Generate compelling headlines and descriptions following proven PPC frameworks.'
    },
    {
      icon: <Target size={24} />,
      title: 'Campaign Analytics',
      description: 'Track performance, identify opportunities, and get actionable recommendations.'
    }
  ];

  const benefits = [
    'Save 10+ hours per week on campaign management',
    'Improve Quality Scores by up to 3 points',
    'Increase CTR with AI-optimized ad copy',
    'Reduce wasted ad spend with smart recommendations',
    'Scale campaigns across multiple languages effortlessly',
    'Get professional-grade ads without hiring an agency'
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logo}>Bidoryx</div>
        <nav style={styles.nav}>
          <a 
            href="/privacy" 
            style={styles.navLink}
            onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
          >
            Privacy Policy
          </a>
          <a 
            href="/terms" 
            style={styles.navLink}
            onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
          >
            Terms of Service
          </a>
          <button
            style={{
              ...styles.navLink,
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid #6366f1',
              borderRadius: '8px',
              padding: '8px 20px',
              cursor: 'pointer',
              color: 'white'
            }}
            onClick={() => navigate('/dashboard')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#6366f1';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Sign In
          </button>
        </nav>
      </header>

      {/* Hero Section */}
      <section style={styles.hero}>
        <h1 style={styles.heroTitle}>
          AI-Powered Google Ads{' '}
          <span style={styles.gradient}>Optimization</span>
        </h1>
        <p style={styles.heroSubtitle}>
          Automate campaign management, generate professional ad copy, and maximize ROI with intelligent bid optimization. Built for advertisers who want agency-grade results without the agency cost.
        </p>
        <button
          style={styles.ctaButton}
          onClick={() => navigate('/dashboard')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 30px rgba(99, 102, 241, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.4)';
          }}
        >
          Get Started Free
          <ArrowRight size={20} />
        </button>
      </section>

      {/* Features Grid */}
      <section style={styles.features}>
        {features.map((feature, index) => (
          <div
            key={index}
            style={styles.featureCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = '#6366f1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            <div style={styles.featureIcon}>
              {feature.icon}
            </div>
            <h3 style={styles.featureTitle}>{feature.title}</h3>
            <p style={styles.featureDescription}>{feature.description}</p>
          </div>
        ))}
      </section>

      {/* Benefits Section */}
      <section style={styles.benefits}>
        <h2 style={styles.benefitsTitle}>Why Choose Bidoryx?</h2>
        <div style={styles.benefitsList}>
          {benefits.map((benefit, index) => (
            <div key={index} style={styles.benefitItem}>
              <CheckCircle size={20} style={{ color: '#10b981', flexShrink: 0, marginTop: '2px' }} />
              <span style={{ fontSize: '15px', color: 'rgba(255, 255, 255, 0.8)' }}>
                {benefit}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.4)' }}>
          © 2026 Bidoryx. All rights reserved.
        </div>
        <div style={styles.footerLinks}>
          <a 
            href="/privacy" 
            style={styles.footerLink}
            onMouseEnter={(e) => e.currentTarget.style.color = '#6366f1'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}
          >
            Privacy Policy
          </a>
          <a 
            href="/terms" 
            style={styles.footerLink}
            onMouseEnter={(e) => e.currentTarget.style.color = '#6366f1'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}
          >
            Terms of Service
          </a>
          <a 
            href="mailto:support@bidoryx.com" 
            style={styles.footerLink}
            onMouseEnter={(e) => e.currentTarget.style.color = '#6366f1'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}
          >
            Contact
          </a>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
